import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { createAddressNumberMask, createPostalCodeMask } from "@ui/input-masks";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { brazilianStates } from "./model";
import { TranslocoCoreModule } from "libs/ui/i18n/src/lib/i18n.module";
import { CommonModule } from "@angular/common";
import { IMaskModule } from "angular-imask";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { GoogleMap, MapMarker } from "@angular/google-maps";
import { GeocodeLocation, GeocodeService } from "./geocode.service";
//import { GetExternalPostalCodeResult } from "shared/api/service-proxies";
import { ProgressBarComponent } from "../progress-bar/progress-bar.component";
import { TranslocoService } from "@jsverse/transloco";
import { GetExternalPostalCodeResult, PostalCodeServiceServiceProxy } from "@api";

@Component({
    selector: "v2-address-editor",
    templateUrl: "./address-editor.component.html",
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        IMaskModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatOptionModule,
        MatSelectModule,
        NgxMatSelectSearchModule,

        // i18n
        TranslocoCoreModule,
        GoogleMap,
        MapMarker,
        ProgressBarComponent,
    ],
    providers: [GeocodeService, PostalCodeServiceServiceProxy],
})
export class V2AddressEditorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    submitting: boolean = false;

    originalPostalCode: any;

    private _unsubscribeAll = new Subject();

    @Input()
    form: FormGroup;

    @Input()
    compactLayout: boolean = false;

    @Input()
    showMandatoryErrors: boolean = false;

    postalCodeMask = createPostalCodeMask();

    numberMask = createAddressNumberMask();

    brazilianStatesFilter = new FormControl();

    statesDatasource = brazilianStates;

    required: boolean;

    @ViewChild("addressNumber") addressNumber: ElementRef;

    @Input() showMap: boolean;

    @Output()
    onAddressChanged: EventEmitter<any>;

    @Output()
    onModelValidChanged: EventEmitter<boolean>;

    public zoom: number;

    center;

    private geocodeService = inject(GeocodeService);

    invalidPostalCode: boolean;

    private _t = inject(TranslocoService);

    private _postalCodeService = inject(PostalCodeServiceServiceProxy);

    private _cdr = inject(ChangeDetectorRef);

    markerOptions = {
        draggable: true,
    };

    constructor() {
        this.onAddressChanged = new EventEmitter();
        this.onModelValidChanged = new EventEmitter();
        this._unsubscribeAll = new Subject();
    }

    ngOnInit() {
        this.zoom = 18;

        const initialPostalCode = this.form.get("postalCode")?.value;
        this.originalPostalCode = initialPostalCode
            ? initialPostalCode.replace(/[\\_() -]/g, "")
            : null;

        this.form
            .get("postalCode")
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                let regex = new RegExp("[\\_() -]?", "g");
                let postalCode = value ? value.replace(regex, "") : "";

                if (postalCode.length == 8) {
                    this.validateAddressPostalCode(postalCode);
                } else {
                    this.invalidPostalCode = false;
                    this.clearAddressData();
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.latitude && this.longitude) {
            this.geocodeService.waitForMapsToLoad().subscribe(() => {
                this.center = { lat: this.latitude, lng: this.longitude };
            });
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!this.showMap) {
            this.addressToCoordinates();
        }
    }
    get postalCode(): string {
        return this.form.getRawValue().postalCode;
    }

    get country(): string {
        return this.form.getRawValue().country;
    }

    get state(): string {
        return this.form.getRawValue().state;
    }

    get city(): string {
        return this.form.getRawValue().city;
    }

    get formattedAddress(): string {
        return this.form.getRawValue().formattedAddress;
    }

    get latitude(): number {
        return this.form.getRawValue().latitude;
    }

    set latitude(value: number) {
        this.form.get("latitude").setValue(value);
    }

    get longitude(): number {
        return this.form.getRawValue().longitude;
    }

    set longitude(value: number) {
        this.form.get("longitude").setValue(value);
    }

    clearAddressData() {
        this.form.patchValue({
            formattedAddress: null,
            reference: null,
            complement: null,
            number: null,
            neighborhood: null,
            city: null,
            state: null,
            country: null,
            latitude: 0,
            longitude: 0,
        });
    }

    validateAddressPostalCode(postalCode: string): void {
        if (postalCode === this.originalPostalCode) {
            return;
        }
        this.submitting = true;
        this._postalCodeService.validatePostalCode(postalCode).subscribe(
            (result: GetExternalPostalCodeResult) => {
                this.submitting = false;
                if (!result.code) {
                    return;
                }
                this.invalidPostalCode = false;
                this.form.patchValue({
                    formattedAddress: result.address,
                    neighborhood: result.neighborhood,
                    city: result.city,
                    state: result.state,
                });
                //Force geocode
                this.latitude = 0;
                this.longitude = 0;
                if (!!this.showMap) {
                    this.addressToCoordinates();
                }
                this.form.markAsDirty();
            },
            () => {
                this.submitting = false;
            },
            () => {
                this.submitting = false;
            }
        );
    }

    addressToCoordinates() {
        if ((this.latitude === undefined || this.latitude === 0 || this.latitude === 0) && !!this.postalCode) {
            let address = this.postalCode.replace("-", "");
            if (this.country) {
                address += " , " + this.country;
            }
            if (this.state) {
                address += " , " + this.state;
            }
            if (this.city) {
                address += " , " + this.city;
            }
            if (this.formattedAddress) {
                address += " , " + this.formattedAddress;
            }
            this.geocodeService.geocodeAddress(address).subscribe((location: GeocodeLocation) => {
                if (location.found == false) {
                    //this.notify.warn(this.l("Address.Map.NotFound"));
                    this.zoom = 3;
                } else {
                    this.zoom = 18;
                }
                this.latitude = location.lat;
                this.longitude = location.lng;
                this.center = { lat: this.latitude, lng: this.longitude };
                this._cdr.detectChanges();
            });
        }
    }

    markerMoved(event: google.maps.MapMouseEvent) {
        if (!event.latLng) {
            return;
        }

        this.latitude = event.latLng.lat();
        this.longitude = event.latLng.lng();

        this.center = { lat: this.latitude, lng: this.longitude };
    }
}
